import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../lib/authedFetch';
import {
  Bell,
  BellOff,
  Flag,
  GitCompare,
  TrendingUp,
  Mail,
  Calendar,
  Clock,
  Check,
  Loader2,
  RotateCcw,
  Save,
  ChevronRight,
  AlertCircle,
  Activity
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHeroCompact } from '../components/layout/PageHeroCompact';
import { getLightSectionTheme } from '../lib/sectionThemes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  flag: Flag,
  'git-compare': GitCompare,
  'trending-up': TrendingUp,
  activity: Activity,
  bell: Bell
};

const CATEGORY_COLORS = {
  benchmark_goals: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600' },
  comparison_alerts: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600' },
  gap_analysis: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600' },
  performance_evidence: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600' },
  general: { bg: 'bg-slate-50', border: 'border-slate-200', icon: 'text-slate-600' }
};

export default function NotificationPreferencesPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [categories, setCategories] = useState([]);
  const [digestOptions, setDigestOptions] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const fetchPreferences = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/users/notification-preferences`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPreferences(data.preferences);
      }
    } catch (error) {
      logger.error('Error fetching preferences:', error);
      toast.error('Failed to load preferences');
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/users/notification-preferences/categories`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
        setDigestOptions(data.digest_options);
      }
    } catch (error) {
      logger.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchPreferences(), fetchCategories()]);
      setLoading(false);
    };
    init();
  }, [fetchPreferences, fetchCategories]);

  const updatePreference = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      const response = await authedFetch(`${API_URL}/api/users/notification-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(preferences)
      });
      
      if (response.ok) {
        toast.success('Preferences saved successfully!');
        setHasChanges(false);
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (error) {
      logger.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    }
    setSaving(false);
  };

  const resetPreferences = async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/users/notification-preferences/reset`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreferences(data.preferences);
        setHasChanges(false);
        toast.success('Preferences reset to defaults');
      }
    } catch (error) {
      logger.error('Error resetting preferences:', error);
      toast.error('Failed to reset preferences');
    }
    setResetDialogOpen(false);
  };

  const renderSettingControl = (setting) => {
    const value = preferences?.[setting.key];
    
    if (setting.type === 'boolean') {
      return (
        <Switch
          checked={value ?? true}
          onCheckedChange={(checked) => updatePreference(setting.key, checked)}
          data-testid={`setting-${setting.key}`}
        />
      );
    }
    
    if (setting.type === 'number') {
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={value ?? 10}
            onChange={(e) => updatePreference(setting.key, parseFloat(e.target.value) || 10)}
            min={setting.min}
            max={setting.max}
            className="w-20"
            data-testid={`setting-${setting.key}`}
          />
          {setting.unit && <span className="text-sm text-slate-500">{setting.unit}</span>}
        </div>
      );
    }
    
    if (setting.type === 'select') {
      return (
        <Select
          value={value ?? setting.options[0]}
          onValueChange={(v) => updatePreference(setting.key, v)}
        >
          <SelectTrigger className="w-32" data-testid={`setting-${setting.key}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {setting.options.map((opt) => (
              <SelectItem key={opt} value={opt} className="capitalize">
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Reengage');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHeroCompact title={`Notification Preferences`} description={`Choose what you want to be notified about.`} theme={__pageTheme} />
    )}>
    <div className="max-w-4xl mx-auto space-y-6" data-testid="notification-preferences-page">
      {/* Header */}
      <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setResetDialogOpen(true)}
            className="text-slate-600"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
          <Button
            onClick={savePreferences}
            disabled={!hasChanges || saving}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-preferences-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <p className="text-sm text-amber-800">You have unsaved changes</p>
        </div>
      )}

      {/* Global Email Settings */}
      <Card className="border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50" data-testid="global-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-indigo-600" />
            Email Settings
          </CardTitle>
          <CardDescription>
            Global email preferences that apply to all notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Master Toggle */}
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
            <div>
              <p className="font-medium text-slate-900">Enable Email Notifications</p>
              <p className="text-sm text-slate-500">Turn off to disable all email notifications</p>
            </div>
            <Switch
              checked={preferences?.email_enabled ?? true}
              onCheckedChange={(checked) => updatePreference('email_enabled', checked)}
              data-testid="email-enabled-toggle"
            />
          </div>
          
          {preferences?.email_enabled && (
            <>
              <Separator />
              
              {/* Digest Settings */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-indigo-600" />
                  <h3 className="font-medium text-slate-900">Email Digest</h3>
                </div>
                <p className="text-sm text-slate-500">
                  Receive a summary instead of individual notifications
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Digest Frequency</Label>
                    <Select
                      value={preferences?.digest_frequency ?? 'none'}
                      onValueChange={(v) => updatePreference('digest_frequency', v)}
                    >
                      <SelectTrigger data-testid="digest-frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {digestOptions?.frequencies?.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {preferences?.digest_frequency === 'weekly' && (
                    <div className="space-y-2">
                      <Label>Day of Week</Label>
                      <Select
                        value={String(preferences?.digest_day ?? 0)}
                        onValueChange={(v) => updatePreference('digest_day', parseInt(v))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {digestOptions?.days?.map((opt) => (
                            <SelectItem key={opt.value} value={String(opt.value)}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {preferences?.digest_frequency !== 'none' && (
                    <div className="space-y-2">
                      <Label>Time of Day</Label>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-slate-400" />
                        <Select
                          value={String(preferences?.digest_hour ?? 9)}
                          onValueChange={(v) => updatePreference('digest_hour', parseInt(v))}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map((hour) => (
                              <SelectItem key={hour} value={String(hour)}>
                                {hour.toString().padStart(2, '0')}:00
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
                
                {preferences?.digest_frequency !== 'none' && (
                  <div className="p-3 bg-indigo-100 rounded-lg">
                    <p className="text-sm text-indigo-800">
                      <strong>Note:</strong> When digest is enabled, you'll receive a summary email 
                      {preferences?.digest_frequency === 'daily' ? ' every day' : ` every ${digestOptions?.days?.find(d => d.value === preferences?.digest_day)?.label || 'Monday'}`}
                      {' '}at {String(preferences?.digest_hour ?? 9).padStart(2, '0')}:00 instead of individual notifications.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Notification Categories */}
      {preferences?.email_enabled && categories.map((category) => {
        const IconComponent = CATEGORY_ICONS[category.icon] || Bell;
        const colors = CATEGORY_COLORS[category.id] || CATEGORY_COLORS.general;
        
        return (
          <Card 
            key={category.id} 
            className={`${colors.bg} ${colors.border} border-2`}
            data-testid={`category-${category.id}`}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IconComponent className={`h-5 w-5 ${colors.icon}`} />
                {category.name}
              </CardTitle>
              <CardDescription>{category.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {category.settings.map((setting, idx) => (
                  <div key={setting.key}>
                    {idx > 0 && <Separator className="my-4" />}
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{setting.label}</p>
                        <p className="text-sm text-slate-500">{setting.description}</p>
                      </div>
                      <div className="ml-4">
                        {renderSettingControl(setting)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Disabled State */}
      {!preferences?.email_enabled && (
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="py-12 text-center">
            <BellOff className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">Email Notifications Disabled</h3>
            <p className="text-slate-500 mt-2">
              Enable email notifications above to configure category-specific settings
            </p>
          </CardContent>
        </Card>
      )}

      {/* Reset Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Notification Preferences?</DialogTitle>
            <DialogDescription>
              This will reset all notification settings to their default values. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={resetPreferences} className="bg-rose-600 hover:bg-rose-700">
              Reset to Defaults
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageShell>
  );
}
